/**
 * Phase 15.5 — the single authoritative post-completion pipeline (D1).
 *
 * Completing a POC/POD must produce identical business outcomes whether the
 * device was online, offline for five minutes, offline for twelve hours, or
 * force-closed in between. Previously email + auto-invoicing + settlement +
 * return-job creation lived INSIDE the native fast path's background IIFE: if
 * the immediate `mobile-*-complete` call failed the completion was queued for
 * replay, but the replay (syncEngine → mobile-pod-complete) never ran any of
 * the post-completion work.
 *
 * The server-owned COMPLETE_APPRAISAL_WORKFLOW now owns return jobs, appraisal
 * email, PDF and invoicing. This legacy durable client intent is retained only
 * for subcontract settlement until that separate workflow moves server-side.
 * It:
 *   1. waits until the server-side completion has actually landed
 *      (no COMPLETE_POC/COMPLETE_POD item left in the offline sync queue),
 *   2. runs subcontract settlement for Delivery.
 *
 * Each step is flagged done and persisted immediately, so a step never runs
 * twice — including across app restarts.
 */

import { triggerSubcontractSettlement } from "@/lib/subcontractSettlement";
import { captureError, captureMetric } from "@/lib/observability";
import type { AppraisalType } from "@/lib/appraisalEmail";
import { isKnownOffline } from "./connectivity";

const STORAGE_KEY = "tm_completion_workflow_intents";
const DONE_KEY = "tm_completion_workflow_done";
const DONE_LIMIT = 200;
const POLL_MS = 30_000;

export interface CompletionWorkflowIntent {
  jobId: string;
  appraisalId: string;
  appraisalType: AppraisalType;
  companyId: string | null;
  /** Empty/absent = no appraisal email should be sent for this completion. */
  customerEmail?: string | null;
  createdAt: number;
  attempts: number;
  done: {
    returnJob?: boolean;
    email?: boolean;
    invoicing?: boolean;
    settlement?: boolean;
  };
}

let intervalId: ReturnType<typeof setInterval> | null = null;
let cleanup: (() => void) | null = null;
let draining = false;

function read(): CompletionWorkflowIntent[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function write(intents: CompletionWorkflowIntent[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(intents));
  } catch { /* storage full — the in-flight attempt still runs */ }
}

/** Appraisal IDs whose workflow already finished — the exactly-once guard. */
function readDone(): string[] {
  try {
    const raw = localStorage.getItem(DONE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function markDone(appraisalId: string): void {
  const done = readDone().filter((id) => id !== appraisalId);
  done.push(appraisalId);
  try {
    localStorage.setItem(DONE_KEY, JSON.stringify(done.slice(-DONE_LIMIT)));
  } catch { /* ignore */ }
}

/** Persist (idempotently) the post-completion workflow and attempt it now. */
export function queueCompletionWorkflow(
  intent: Omit<CompletionWorkflowIntent, "createdAt" | "attempts" | "done">,
): void {
  const existing = read();
  if (readDone().includes(intent.appraisalId)
      || existing.some((i) => i.appraisalId === intent.appraisalId)) {
    void processCompletionWorkflowQueue();
    return;
  }
  existing.push({ ...intent, createdAt: Date.now(), attempts: 0, done: {} });
  write(existing);
  captureMetric("appraisal.completion", "queued", 0, {
    appraisalId: intent.appraisalId, jobId: intent.jobId, appraisalType: intent.appraisalType,
  });
  void processCompletionWorkflowQueue();
}

export function getPendingCompletionWorkflowCount(): number {
  return read().length;
}

/** True once the server has the completion (nothing left queued for replay). */
async function completionSynced(jobId: string): Promise<boolean> {
  try {
    const { syncQueue } = await import("@/lib/offlineDb");
    const all = await syncQueue.getAll();
    return !all.some(
      (i) => i.job_id === jobId && (i.type === "COMPLETE_POC" || i.type === "COMPLETE_POD"),
    );
  } catch {
    // No offline DB (web) — the completion call was awaited inline.
    return true;
  }
}

/** Drain every intent whose completion has landed on the server. */
export async function processCompletionWorkflowQueue(): Promise<void> {
  if (draining) return;
  if (isKnownOffline()) return;

  draining = true;
  try {
    const intents = read();
    if (intents.length === 0) return;

    const remaining: CompletionWorkflowIntent[] = [];

    for (const intent of intents) {
      if (!(await completionSynced(intent.jobId))) {
        remaining.push(intent);
        continue;
      }

      const isDelivery = intent.appraisalType === "delivery";
      let failed = false;

      // Return-job creation, PDF, appraisal email and invoicing are exclusively
      // server-owned. Keeping them here caused two dispatchers to race.
      intent.done.returnJob = true;
      intent.done.email = true;
      intent.done.invoicing = true;

      // Subcontract settlement (delivery only).
      if (isDelivery && !intent.done.settlement) {
        try {
          await triggerSubcontractSettlement(intent.jobId);
          intent.done.settlement = true;
          write(intents);
        } catch (err) {
          failed = true;
          captureError("appraisal.completion.settlement", err, {
            severity: "warn", metadata: { jobId: intent.jobId },
          });
        }
      }

      if (failed) {
        remaining.push({ ...intent, attempts: intent.attempts + 1 });
      } else {
        markDone(intent.appraisalId);
        captureMetric("appraisal.completion", "workflow_complete", Date.now() - intent.createdAt, {
          appraisalId: intent.appraisalId, jobId: intent.jobId, attempts: intent.attempts,
        });
      }
    }

    write(remaining);
  } catch (err) {
    captureError("appraisal.completion.drain", err);
  } finally {
    draining = false;
  }
}

export function startCompletionWorkflowQueue(): void {
  if (intervalId) return;
  void processCompletionWorkflowQueue();
  intervalId = setInterval(() => { void processCompletionWorkflowQueue(); }, POLL_MS);

  const onOnline = () => { void processCompletionWorkflowQueue(); };
  const onVisible = () => {
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      void processCompletionWorkflowQueue();
    }
  };
  window.addEventListener("online", onOnline);
  window.addEventListener("network-status-change", onOnline as EventListener);
  document.addEventListener("visibilitychange", onVisible);
  cleanup = () => {
    window.removeEventListener("online", onOnline);
    window.removeEventListener("network-status-change", onOnline as EventListener);
    document.removeEventListener("visibilitychange", onVisible);
  };
}

export function stopCompletionWorkflowQueue(): void {
  if (intervalId) { clearInterval(intervalId); intervalId = null; }
  if (cleanup) { cleanup(); cleanup = null; }
}
